/**
 * Stone Investment Developments List View
 *
 * Full-page developments listing with Stone Investment's luxury portfolio aesthetic.
 * Mirrors the feature set of AllysProjectsListView (search, filters, sort, grid)
 * while using the Stone design language: greyscale palette, Cormorant Garamond +
 * Montserrat typography, square corners, and editorial layouts.
 *
 * Features a cinematic hero carousel that cycles through project hero images
 * with Ken Burns zoom and crossfade transitions.
 *
 * Uses StoneDevelopmentCard / StoneDevelopmentGrid for the card grid.
 */

'use client';

import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import { Building2, Search, SlidersHorizontal, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { StoneCinematicHeroCarousel, type CinematicHeroImage } from './StoneCinematicHeroCarousel';
import { StoneDevelopmentCard, type DevelopmentCardData } from './StoneDevelopmentCard';

// ============================================================================
// Types
// ============================================================================

export interface StoneDevelopmentsListViewProps {
  projects: PropertyDevelopmentProject[];
  organisationId: string;
  locale?: string;
  messages: {
    title: string;
    subtitle: string;
    filterAll: string;
    filterActive: string;
    filterUpcoming: string;
    filterCompleted: string;
    sortNewest: string;
    sortOldest: string;
    sortName: string;
    noProjects: string;
    noProjectsDescription: string;
    availableUnits: string;
    soldOut: string;
    comingSoon: string;
    enquireNow: string;
    viewProject: string;
    startingFrom: string;
    sortBy: string;
    filterBy: string;
    projects: string;
    searchPlaceholder?: string;
    filters?: string;
    home?: string;
    developments?: string;
    project?: string;
    clearAllFilters?: string;
    exploreProject?: string;
    active?: string;
    total?: string;
  };
  /** Full messages object for nested component translations */
  allMessages?: Record<string, unknown>;
  className?: string;
}

type FilterType = 'ALL' | 'ACTIVE' | 'UPCOMING' | 'COMPLETED' | 'SOLD_OUT';
type SortType = 'newest' | 'oldest' | 'name';

// ============================================================================
// Helpers
// ============================================================================

function mapProjectToCardData(project: PropertyDevelopmentProject): DevelopmentCardData {
  return {
    id: project.id,
    name: project.name,
    slug: project.slug,
    description: project.description,
    status: (project.status as DevelopmentCardData['status']) ?? null,
    stage: (project.stage as DevelopmentCardData['stage']) ?? null,
    projectType: (project.projectType as DevelopmentCardData['projectType']) ?? null,
    totalUnits: project.totalUnits,
    soldUnits: project.soldUnits,
    availableUnits: project.availableUnits,
    unitMixSummary: project.unitMixSummary,
    estimatedStartDate: project.estimatedStartDate,
    estimatedCompletionDate: project.estimatedCompletionDate,
    actualCompletionDate: project.actualCompletionDate,
    displayPriceText: project.displayPriceText,
    marketingStatusText: project.marketingStatusText,
    heroImageUrl: project.heroImageUrl,
    imageUrls: project.imageUrls,
  };
}

// ============================================================================
// Component
// ============================================================================

export function StoneDevelopmentsListView({
  projects,
  organisationId: _organisationId,
  locale = 'en',
  messages,
  allMessages,
  className = '',
}: StoneDevelopmentsListViewProps) {
  const [filter, setFilter] = useState<FilterType>('ALL');
  const [sort, setSort] = useState<SortType>('newest');
  const [searchValue, setSearchValue] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const searchFn = useCallback(
    (project: PropertyDevelopmentProject, query: string): boolean => {
      const q = query.toLowerCase();
      return !!(
        project.name.toLowerCase().includes(q) ||
        project.description?.toLowerCase().includes(q) ||
        project.slug?.toLowerCase().includes(q)
      );
    },
    [],
  );

  const filteredAndSorted = useMemo(() => {
    let result = [...projects];

    if (searchValue) {
      result = result.filter((p) => searchFn(p, searchValue));
    }

    if (filter !== 'ALL') {
      result = result.filter((p) => {
        if (filter === 'COMPLETED') {
          return p.status === 'COMPLETED_ARCHIVED' || p.actualCompletionDate;
        }
        return p.status === filter;
      });
    }

    result.sort((a, b) => {
      switch (sort) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return result;
  }, [projects, filter, sort, searchValue, searchFn]);

  const statusCounts = useMemo(
    () => ({
      ALL: projects.length,
      ACTIVE: projects.filter((p) => p.status === 'ACTIVE').length,
      UPCOMING: projects.filter((p) => p.status === 'UPCOMING').length,
      COMPLETED: projects.filter(
        (p) => p.status === 'COMPLETED_ARCHIVED' || p.actualCompletionDate,
      ).length,
      SOLD_OUT: projects.filter((p) => p.status === 'SOLD_OUT').length,
    }),
    [projects],
  );

  const hasActiveFilters = filter !== 'ALL' || !!searchValue;

  const heroImages = useMemo<CinematicHeroImage[]>(() => {
    const seen = new Set<string>();
    const result: CinematicHeroImage[] = [];

    for (const project of projects) {
      const urls: string[] = [];
      if (project.heroImageUrl) urls.push(project.heroImageUrl);
      if (project.imageUrls?.length) {
        for (const url of project.imageUrls) {
          if (url) urls.push(url);
        }
      }

      for (const url of urls) {
        if (!seen.has(url)) {
          seen.add(url);
          result.push({ url, alt: project.name });
        }
      }
    }

    return result;
  }, [projects]);

  return (
    <div className={`min-h-screen bg-stone-50 page-content ${className}`}>
      {/* ------------------------------------------------------------------ */}
      {/* Cinematic Hero                                                     */}
      {/* ------------------------------------------------------------------ */}
      {heroImages.length > 0 ? (
        <StoneCinematicHeroCarousel
          images={heroImages}
          height="h-[55vh] sm:h-[60vh] lg:h-[65vh]"
          autoPlayInterval={5500}
        >
          <div className="absolute inset-0 z-30 flex flex-col justify-end pointer-events-none">
            <div className="mx-auto w-full max-w-7xl px-6 md:px-12 pb-14 sm:pb-16 lg:pb-20">
              <nav className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 font-sans pointer-events-auto">
                <a
                  href={`/${locale}`}
                  className="hover:text-white transition-colors duration-300"
                >
                  {messages.home || 'Home'}
                </a>
                <span className="text-white/30">/</span>
                <span className="text-white/90">
                  {messages.developments || 'Developments'}
                </span>
              </nav>

              <h1
                className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[0.95] tracking-tight mb-3"
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  textShadow: '0 4px 40px rgba(0,0,0,0.4)',
                }}
              >
                {messages.title}
              </h1>

              {messages.subtitle && (
                <p
                  className="text-base sm:text-lg text-white/70 font-sans max-w-2xl mb-6"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    textShadow: '0 2px 20px rgba(0,0,0,0.3)',
                  }}
                >
                  {messages.subtitle}
                </p>
              )}

              <div className="flex items-center gap-3 flex-wrap pointer-events-auto">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-xs text-white/80 font-sans uppercase tracking-wider border border-white/10">
                  <span className="font-semibold text-white">
                    {projects.length}
                  </span>
                  {messages.total || 'Total'}
                </span>
                {statusCounts.UPCOMING > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-xs text-white/80 font-sans uppercase tracking-wider border border-white/10">
                    <span className="font-semibold text-white">
                      {statusCounts.UPCOMING}
                    </span>
                    {messages.comingSoon || 'Coming Soon'}
                  </span>
                )}
                {statusCounts.ACTIVE > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/90 text-xs text-stone-900 font-sans uppercase tracking-wider">
                    <span className="font-semibold">
                      {statusCounts.ACTIVE}
                    </span>
                    {messages.active || 'Active'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </StoneCinematicHeroCarousel>
      ) : (
        <div className="relative bg-stone-900 h-[40vh] sm:h-[45vh] flex flex-col justify-end overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/50 to-stone-800/30" />
          <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12 pb-14 sm:pb-16 lg:pb-20">
            <nav className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 font-sans">
              <a
                href={`/${locale}`}
                className="hover:text-white transition-colors duration-300"
              >
                {messages.home || 'Home'}
              </a>
              <span className="text-white/30">/</span>
              <span className="text-white/90">
                {messages.developments || 'Developments'}
              </span>
            </nav>

            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[0.95] tracking-tight mb-3"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {messages.title}
            </h1>

            {messages.subtitle && (
              <p
                className="text-base sm:text-lg text-white/70 font-sans max-w-2xl mb-6"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {messages.subtitle}
              </p>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-xs text-white/80 font-sans uppercase tracking-wider border border-white/10">
                <span className="font-semibold text-white">
                  {projects.length}
                </span>
                {messages.total || 'Total'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Sticky Toolbar                                                     */}
      {/* ------------------------------------------------------------------ */}
      <div className="sticky top-[var(--nav-offset-top,0)] z-40 bg-white border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex items-center gap-4 py-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search size={16} className="text-stone-400" strokeWidth={1.5} />
              </div>
              <input
                type="text"
                placeholder={messages.searchPlaceholder || 'Search developments...'}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="block w-full border border-stone-200 bg-stone-50 py-2 pl-9 pr-3 text-sm text-stone-900 font-sans placeholder:text-stone-400 focus:bg-white focus:border-stone-400 focus:outline-none transition-all"
              />
              {searchValue && (
                <button
                  onClick={() => setSearchValue('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-stone-400 hover:text-stone-700"
                >
                  <X size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>

            <div className="hidden sm:block h-6 w-px bg-stone-200" />

            {/* Desktop Filter Pills */}
            <div className="hidden lg:flex items-center gap-2">
              {(
                [
                  { id: 'ALL', label: messages.filterAll, count: statusCounts.ALL },
                  { id: 'ACTIVE', label: messages.filterActive, count: statusCounts.ACTIVE },
                  { id: 'UPCOMING', label: messages.filterUpcoming, count: statusCounts.UPCOMING },
                ] as const
              ).map((status) => (
                <button
                  key={status.id}
                  onClick={() => setFilter(status.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans uppercase tracking-wider transition-all duration-300 ${
                    filter === status.id
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200 border border-stone-200'
                  }`}
                >
                  {status.label}
                  {status.count > 0 && (
                    <span
                      className={`px-1.5 py-0.5 text-[10px] font-semibold ${
                        filter === status.id
                          ? 'bg-white/20 text-white'
                          : 'bg-stone-200 text-stone-500'
                      }`}
                    >
                      {status.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="hidden lg:block h-6 w-px bg-stone-200" />

            {/* Sort */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xs text-stone-500 font-sans uppercase tracking-wider">
                {messages.sortBy || 'Sort'}
              </span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortType)}
                className="appearance-none bg-transparent text-sm font-sans font-medium text-stone-700 cursor-pointer focus:outline-none pr-6"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2378716c'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0 center',
                  backgroundSize: '16px',
                }}
              >
                <option value="newest">{messages.sortNewest}</option>
                <option value="oldest">{messages.sortOldest}</option>
                <option value="name">{messages.sortName}</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="hidden md:flex items-center gap-2 text-sm text-stone-500 font-sans ml-auto">
              <span className="font-semibold text-stone-900">
                {filteredAndSorted.length}
              </span>
              <span>
                {filteredAndSorted.length === 1
                  ? messages.project || 'project'
                  : messages.projects || 'projects'}
              </span>
            </div>

            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setMobileFiltersOpen((v) => !v)}
              className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 bg-stone-100 text-sm font-sans text-stone-700 hover:bg-stone-200 transition-colors border border-stone-200"
            >
              <SlidersHorizontal size={14} strokeWidth={1.5} />
              <span className="hidden sm:inline">
                {messages.filters || 'Filters'}
              </span>
              {hasActiveFilters && (
                <span className="flex h-4 w-4 items-center justify-center bg-stone-900 text-[10px] font-bold text-white">
                  {(filter !== 'ALL' ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Filter Panel */}
          {mobileFiltersOpen && (
            <div className="lg:hidden border-t border-stone-200 py-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    { id: 'ALL', label: messages.filterAll, count: statusCounts.ALL },
                    { id: 'ACTIVE', label: messages.filterActive, count: statusCounts.ACTIVE },
                    { id: 'UPCOMING', label: messages.filterUpcoming, count: statusCounts.UPCOMING },
                    { id: 'COMPLETED', label: messages.filterCompleted, count: statusCounts.COMPLETED },
                  ] as const
                ).map((status) => (
                  <button
                    key={status.id}
                    onClick={() => setFilter(status.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans uppercase tracking-wider transition-all duration-300 ${
                      filter === status.id
                        ? 'bg-stone-900 text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200 border border-stone-200'
                    }`}
                  >
                    {status.label}
                    <span
                      className={`text-[10px] ${
                        filter === status.id ? 'text-white/70' : 'text-stone-400'
                      }`}
                    >
                      ({status.count})
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 sm:hidden">
                <span className="text-xs text-stone-500 font-sans uppercase tracking-wider">
                  {messages.sortBy || 'Sort'}
                </span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortType)}
                  className="flex-1 border border-stone-200 bg-white px-3 py-2 text-sm font-sans focus:outline-none focus:border-stone-400"
                >
                  <option value="newest">{messages.sortNewest}</option>
                  <option value="oldest">{messages.sortOldest}</option>
                  <option value="name">{messages.sortName}</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={() => {
                    setFilter('ALL');
                    setSearchValue('');
                  }}
                  className="text-xs text-stone-600 hover:text-stone-900 font-sans uppercase tracking-wider underline underline-offset-2"
                >
                  {messages.clearAllFilters || 'Clear all filters'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Development Grid                                                   */}
      {/* ------------------------------------------------------------------ */}
      <div className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {filteredAndSorted.length > 0 ? (
            <div className="grid gap-12 grid-cols-1 md:grid-cols-2">
              {filteredAndSorted.map((project, index) => {
                const cardData = mapProjectToCardData(project);
                const isFeatured = index === 0 && filteredAndSorted.length > 1;

                return (
                  <div
                    key={project.id}
                    className={isFeatured ? 'md:col-span-2' : ''}
                  >
                    <StoneDevelopmentCard
                      development={cardData}
                      href={`/${locale}/projects/${project.slug || project.id}`}
                      variant={isFeatured ? 'featured' : 'default'}
                      showUnitsProgress
                      showTimeline
                      locale={locale}
                      messages={allMessages}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
              <div className="w-20 h-20 border border-stone-300 flex items-center justify-center mb-8">
                <Building2 size={40} className="text-stone-400" strokeWidth={1} />
              </div>
              <h3 className="font-serif text-3xl text-stone-900 mb-3 italic">
                {messages.noProjects}
              </h3>
              <p className="text-stone-500 font-sans text-sm tracking-wide max-w-md">
                {messages.noProjectsDescription}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StoneDevelopmentsListView;
